import React, { useEffect, useState } from "react";
import { Box, Button, TextField, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Typography, Dialog, DialogActions, DialogContent, DialogTitle, } from "@mui/material";
import { ReactComponent as EditIcon } from "assets/icons/EditIcon.svg";
import { LuTrash2 } from "react-icons/lu";
import CustomButton from "components/Button";
import { FaPlus } from "react-icons/fa6";
import { useCreateMasterDataMutation, useUpdateMasterDataMutation, useDeleteMasterDataMutation } from "store/slices/apis/masterDataApiSlice";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { LuEye, LuRefreshCw, LuSearch } from "react-icons/lu";

const CrudSection = ({ module, label, category, rawData = [], isLoading, defaultItems = [] }) => {
    const dispatch = useDispatch();
    const [items, setItems] = useState([]);
    const [newItem, setNewItem] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    const [editIndex, setEditIndex] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [openEditDialog, setOpenEditDialog] = useState(false);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(null);
    const [inputError, setInputError] = useState("");

    const [createMasterData] = useCreateMasterDataMutation();
    const [updateMasterData] = useUpdateMasterDataMutation();
    const [deleteMasterData] = useDeleteMasterDataMutation();

    const isInitialLoading = isLoading && items.length === 0;

    useEffect(() => {
        if (Array.isArray(defaultItems)) {
            setItems(defaultItems);
        }
    }, [defaultItems]);

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    // ADD
    const handleAdd = async () => {
        if (!newItem.trim()) {
            setInputError("Please enter text");
            return;
        }

        const body = {
            module,
            category,
            value: newItem,
        };

        try {
            const response = await createMasterData({ body }).unwrap();
            openNotification("Master data value created successfully", "success");
            setNewItem("");
        } catch (error) {
            openNotification(error?.error ?? "Something went wrong", "error");
        }

    };

    // EDIT
    const handleEdit = (index) => {
        setEditIndex(index);
        setEditValue(items[index]);
        setOpenEditDialog(true);
    };

    const saveEdit = async () => {
        const selected = rawData[editIndex];

        const body = {
            configurationId: selected.configurationId,
            module,
            category,
            value: editValue,
        };

        try {
            const response = await updateMasterData({ body }).unwrap();
            openNotification("Master data value updated successfully", "success");
            setOpenEditDialog(false);
        } catch (error) {
            openNotification(error?.error ?? "Something went wrong", "error");
        }

    };

    // DELETE
    const handleDelete = (index) => {
        setDeleteIndex(index);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        const selected = rawData[deleteIndex];

        try {
            const response = await deleteMasterData(selected.configurationId).unwrap();
            openNotification("Master data value deleted successfully", "success");
        } catch (error) {
            openNotification(error?.error ?? "Something went wrong", "error");
        } finally {
            setOpenDeleteDialog(false);
            setDeleteIndex(null);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const filteredItems = items.filter((item) =>
        item.toLowerCase().includes(searchTerm.toLowerCase())
    );


    return (
        <Box sx={{ p: 2 }}>
            <Typography
                sx={{
                    fontWeight: 650,
                    fontSize: "18px",
                    lineHeight: "25px",
                    color: "#0F172A",
                    fontFamily: "Outfit, sans-serif",
                    mb: 1.5,
                }}>
                {label}
            </Typography>

            {/* ADD NEW ITEM */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { md: "row", xs: "column" },
                    justifyContent: "space-between",
                    alignItems: { md: "flex-start", xs: "stretch" },
                    gap: 1.5,
                    pb: 1.5,
                    borderBottom: "1px solid #E2E8F0",
                }}
            >
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>

                    <TextField
                        placeholder={`Add ${label}`}
                        size="small"
                        value={newItem}
                        onChange={(e) => {
                            setNewItem(e.target.value);
                            if (e.target.value.trim()) setInputError("");
                        }}
                        error={!!inputError}
                        helperText={inputError}
                        sx={{
                            width: 250,
                            "& .MuiOutlinedInput-root": {
                                minHeight: 36,
                                borderRadius: "3px",
                                fontSize: 13,
                                "& fieldset": {
                                    borderColor: "#D0D7DE", // default border
                                },
                                "&:hover fieldset": {
                                    borderColor: "#5F5F5F", // hover border
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "#2563EB", // focus border
                                },
                                "&.Mui-error fieldset": {
                                    borderColor: "#5F5F5F", // error border
                                },
                            },
                        }}
                    />


                    <Box>
                        <CustomButton
                            label="Add"
                            startIcon={<FaPlus />}
                            color="primary"
                            onClick={handleAdd}
                            sx={{
                                backgroundColor: "#2563EB",
                                fontFamily: 'Outfit',
                                fontWeight: 600,
                                fontSize: '13px',
                                lineHeight: '100%',
                                letterSpacing: '0px',
                                px: 2,
                                py: 1.2,
                            }}
                        />
                    </Box>
                </Box>
                <TextField
                    placeholder="Search By Entity Name"
                    value={searchTerm}
                    // width="100%"
                    onChange={handleSearchChange}
                    variant="outlined"
                    size="small"
                    InputProps={{
                        startAdornment: <LuSearch style={{ marginRight: '8px', color: '#6B7280' }} />,
                    }}
                    sx={{
                        minWidth: { md: '320px', xs: "100%" },
                        backgroundColor: '#fff',
                        '& .MuiOutlinedInput-root': {
                            minHeight: '36px',
                            borderRadius: '3px',
                            fontSize: '13px',
                            '& fieldset': { borderColor: '#D0D7DE' },
                            '&:hover fieldset': { borderColor: '#94A3B8' },
                            '&.Mui-focused fieldset': { borderColor: '#2563EB', borderWidth: 1 },
                        },
                        '& .MuiInputBase-input::placeholder': {
                            fontSize: '13px',
                            color: '#94A3B8',
                            opacity: 1,
                        }
                    }}
                    type="search"
                />
            </Box>

            {/* LIST ITEMS */}
            {isInitialLoading ? (
                <Typography sx={{ ml: 1 }}>Loading...</Typography>
            ) : filteredItems.length === 0 ? (
                <Box
                    sx={{
                        border: "1px solid #ddd",
                        borderRadius: 1,
                        p: 4,
                        textAlign: "center",
                        color: "#666",
                        fontSize: "14px",
                        backgroundColor: "#F9FBFC",
                        fontFamily: "Outfit, sans-serif",
                    }}
                >
                    Records not available.
                </Box>
            ) : (
                <List sx={{ border: "1px solid #ddd", borderRadius: 1 }}>
                    {filteredItems.map((item, index) => {
                        const isLast = index === items.length - 1;
                        return (
                            <ListItem key={index} divider={!isLast} sx={{
                                backgroundColor: "#F9FBFC",
                                "&:hover": {
                                    backgroundColor: "#EEF4FF",
                                },
                                py: 1.2,
                                px: 2,
                            }}>
                                <ListItemText primary={item}
                                    primaryTypographyProps={{
                                        fontSize: "14px",
                                        color: "#333",
                                        fontFamily: "Outfit, sans-serif",
                                    }}
                                />

                                <ListItemSecondaryAction>
                                    <IconButton onClick={() => handleEdit(index)}>
                                        <EditIcon htmlColor="#84919A" style={{ width: 15, height: 15 }} />
                                    </IconButton>

                                    <IconButton onClick={() => handleDelete(index)}>
                                        <LuTrash2 style={{ color: "#84919A", width: 15, height: 15 }} />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        )
                    })}
                </List>
            )}

            {/* EDIT DIALOG */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} PaperProps={{
                sx: { width: 400, height: 250 },
            }}>
                <DialogTitle sx={{ bgcolor: "#ECECEC" }}>Edit {category}</DialogTitle>

                <DialogContent sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                    />
                </DialogContent>

                <DialogActions>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 1,
                            width: "100%",
                            boxSizing: "border-box",
                            overflowX: "hidden",
                            mt: 2,
                            mb: 1,
                            mr: 1,
                            justifyContent: "flex-end",
                        }}
                    >
                        <Button onClick={() => setOpenEditDialog(false)}
                            sx={{
                                textTransform: "none",
                                color: "#475569",
                                fontWeight: 500,
                                fontSize: "13px",
                                lineHeight: "100%",
                                fontFamily: "Outfit, sans-serif",
                            }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={saveEdit}
                            variant="contained"
                            sx={{
                                backgroundColor: "#2563EB",
                                borderRadius: "3px",
                                textTransform: "none",
                                fontWeight: 500,
                                fontSize: "13px",
                                lineHeight: "100%",
                                fontFamily: "Outfit, sans-serif",
                                color: "#FFFFFF",
                                px: "18px",
                                py: "10px",
                            }}
                        >
                            Save
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* DELETE DIALOG */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} PaperProps={{
                sx: { width: 400, height: 250 },
            }}>
                <DialogTitle sx={{ bgcolor: "#ECECEC" }}>Confirm Delete</DialogTitle>

                <DialogContent sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: "14px", color: "#444" }}>
                        Are you sure you want to delete this {label} value?
                    </Typography>

                    {deleteIndex !== null && (
                        <Typography sx={{ mt: 1, fontWeight: 500, color: "#000" }}>
                            "{items[deleteIndex]}"
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 1,
                            width: "100%",
                            boxSizing: "border-box",
                            overflowX: "hidden",
                            mt: 2,
                            mb: 1,
                            mr: 1,
                            justifyContent: "flex-end",
                        }}
                    >
                        <Button onClick={() => setOpenDeleteDialog(false)}
                            sx={{
                                textTransform: "none",
                                color: "#475569",
                                fontWeight: 500,
                                fontSize: "13px",
                                lineHeight: "100%",
                                fontFamily: "Outfit, sans-serif",
                            }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDelete}
                            variant="contained"
                            sx={{
                                backgroundColor: "#DC2626",
                                borderRadius: "3px",
                                textTransform: "none",
                                fontWeight: 500,
                                fontSize: "13px",
                                lineHeight: "100%",
                                fontFamily: "Outfit, sans-serif",
                                color: "#FFFFFF",
                                px: "18px",
                                py: "10px",
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CrudSection;
